import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "动漫分类", to: "/category" },
  { label: "动漫资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

export default function SiteNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !menuOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors",
      transparent
        ? "text-background-50/90 hover:text-background-50"
        : "text-foreground-700 hover:text-primary-600",
      isActive
        ? transparent
          ? "text-background-50"
          : "text-primary-600"
        : "",
    ].join(" ");

  return (
    <header
      className={[
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        transparent
          ? "bg-transparent"
          : "border-b border-background-200/80 bg-background-50/95 backdrop-blur-md",
      ].join(" ")}
    >
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span
            className={[
              "flex h-10 w-10 items-center justify-center rounded-lg text-xl",
              transparent ? "bg-primary-500 text-background-50" : "bg-primary-500 text-background-50",
            ].join(" ")}
          >
            <i className="ri-tv-2-line" />
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={[
                "font-heading text-lg font-extrabold tracking-tight md:text-xl",
                transparent ? "text-background-50" : "text-foreground-950",
              ].join(" ")}
            >
              动漫天堂
            </span>
            <span
              className={[
                "mt-1 text-[10px] uppercase tracking-[0.3em]",
                transparent ? "text-background-200" : "text-foreground-400",
              ].join(" ")}
            >
              Anime Paradise
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={linkClass}>
              {({ isActive }) => (
                <span className="flex flex-col items-center gap-1">
                  {item.label}
                  <span
                    className={[
                      "h-[2px] w-4 rounded-full transition-all",
                      isActive ? "bg-primary-500 opacity-100" : "opacity-0",
                    ].join(" ")}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={[
              "hidden h-10 items-center gap-2 rounded-full border px-4 transition-colors md:flex",
              transparent
                ? "border-background-50/40 bg-background-50/10"
                : "border-background-300 bg-background-100",
            ].join(" ")}
          >
            <i
              className={[
                "ri-search-line text-base",
                transparent ? "text-background-100" : "text-foreground-500",
              ].join(" ")}
            />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索动漫 / 资讯"
              className={[
                "w-36 bg-transparent text-sm outline-none xl:w-44",
                transparent
                  ? "text-background-50 placeholder:text-background-200/80"
                  : "text-foreground-800 placeholder:text-foreground-400",
              ].join(" ")}
            />
          </form>

          <Link
            to="/category"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 md:inline-flex"
          >
            开始探索
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((prev) => !prev)}
            className={[
              "flex h-10 w-10 items-center justify-center rounded-md border text-xl transition-colors md:hidden",
              transparent
                ? "border-background-50/40 text-background-50"
                : "border-background-300 text-foreground-800",
            ].join(" ")}
          >
            <i className={menuOpen ? "ri-close-line" : "ri-menu-3-line"} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-4 flex h-11 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索动漫 / 资讯"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="whitespace-nowrap text-sm font-semibold text-primary-600">
              搜索
            </button>
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "flex items-center justify-between rounded-md px-3 py-3 text-base font-medium",
                    isActive ? "bg-primary-50 text-primary-700" : "text-foreground-800",
                  ].join(" ")
                }
              >
                <span>{item.label}</span>
                <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
              </NavLink>
            ))}
            <NavLink
              to="/search"
              className="flex items-center justify-between rounded-md px-3 py-3 text-base font-medium text-foreground-800"
            >
              <span>搜索</span>
              <i className="ri-search-line text-lg text-foreground-400" />
            </NavLink>
          </div>
        </div>
      )}
    </header>
  );
}